import { FinancialGoal } from "@rentablo/api/apiv1";
import { endOfMonthIso, plannedWealthAt, toIsoDate } from "./goalMath";

export type GoalChartRange = "fiveYears" | "tenYears" | "untilTarget";

export type ChartPoint = [number, number];

export interface GoalBar {
  label: string;
  actual: number | null;
  plan: number;
  isCurrent: boolean;
  isFuture: boolean;
}

export interface HistoryPoint {
  /** yyyy-MM-dd */
  date: string;
  value: number;
}

export const toUtcTime = (iso: string) => {
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number);
  return Date.UTC(year, month - 1, day);
};

const yearOf = (iso: string) => Number(iso.slice(0, 4));
const monthOf = (iso: string) => Number(iso.slice(5, 7)) - 1;

/** Goals running longer than this offer the 5 / 10 years / target ranges */
export const RANGE_CHOICE_MIN_YEARS = 10;

export const hasRangeChoice = ({
  startDate = "",
  endDate = "",
}: FinancialGoal) =>
  yearOf(endDate) - yearOf(startDate) > RANGE_CHOICE_MIN_YEARS;

/** The chart shows the goal from its start to the end of the range */
export const goalWindowEnd = (
  goal: FinancialGoal,
  range: GoalChartRange,
  today: string
) => {
  const target = (goal.endDate ?? today).slice(0, 10);
  if (range === "untilTarget" || !hasRangeChoice(goal)) return target;
  const years = range === "fiveYears" ? 5 : 10;
  const rangeEnd = endOfMonthIso(yearOf(today) + years, 11);
  return rangeEnd < target ? rangeEnd : target;
};

const planOf = (goal: FinancialGoal) => ({
  startDate: (goal.startDate ?? "").slice(0, 10),
  initialWealth: goal.initialWealth ?? 0,
  savingsPerMonth: goal.savingsPerMonth ?? 0,
});

const isRunning = (goal: FinancialGoal, today: string) =>
  (goal.startDate ?? "").slice(0, 10) <= today &&
  today <= (goal.endDate ?? "").slice(0, 10);

/**
 * Plan at every month end of the window. Today's point is the API's
 * expectedAssets, so the line meets the Plan figure shown above the chart.
 * Without a start date there is no plan to draw.
 */
export const planSeries = (
  goal: FinancialGoal,
  windowEnd: string,
  today: string
): ChartPoint[] => {
  if (!goal.startDate) return [];
  const plan = planOf(goal);
  const yieldPerYear = goal.requiredYieldPerYear ?? 0;
  const points: ChartPoint[] = [
    [toUtcTime(plan.startDate), plan.initialWealth],
  ];
  const startYear = yearOf(plan.startDate);
  const startMonth = monthOf(plan.startDate);

  for (let offset = 0; ; offset++) {
    const monthEnd = endOfMonthIso(startYear, startMonth + offset);
    if (monthEnd > windowEnd) break;
    if (monthEnd !== today) {
      points.push([
        toUtcTime(monthEnd),
        plannedWealthAt(plan, yieldPerYear, monthEnd),
      ]);
    }
  }

  if (isRunning(goal, today) && today <= windowEnd) {
    points.push([toUtcTime(today), goal.expectedAssets ?? 0]);
  }
  return points.sort(([a], [b]) => a - b);
};

/** History up to yesterday plus the API's actualAssets for today */
export const actualSeries = (
  goal: FinancialGoal,
  history: HistoryPoint[],
  today: string
): ChartPoint[] => {
  const points: ChartPoint[] = history
    .filter(({ date }) => date < today)
    .map(({ date, value }) => [toUtcTime(date), value]);
  if (isRunning(goal, today)) {
    points.push([toUtcTime(today), goal.actualAssets ?? 0]);
  }
  return points;
};

interface Period {
  label: string;
  end: string;
}

/** Whether the last time lies at most three calendar years after the first */
export const isWithinThreeYears = (first: number, last: number) => {
  const start = new Date(first);
  return (
    last <=
    Date.UTC(
      start.getUTCFullYear() + 3,
      start.getUTCMonth(),
      start.getUTCDate()
    )
  );
};

const periodsOf = (start: string, end: string): Period[] => {
  const quarterly = isWithinThreeYears(toUtcTime(start), toUtcTime(end));
  const periods: Period[] = [];
  const step = quarterly ? 3 : 12;
  const firstMonth = quarterly ? monthOf(start) - (monthOf(start) % 3) : 0;

  for (let month = firstMonth; ; month += step) {
    const year = yearOf(start) + Math.floor(month / 12);
    const periodEnd = endOfMonthIso(year, (month % 12) + step - 1);
    const label = quarterly
      ? `Q${Math.floor((month % 12) / 3) + 1} '${String(year).slice(2)}`
      : String(year);
    periods.push({ label, end: periodEnd < end ? periodEnd : end });
    if (periodEnd >= end) return periods;
  }
};

/**
 * One pair per year (per quarter for windows up to three years): Vermögen
 * and Plan at the period's end; the current period compares today's values,
 * later periods have the plan only.
 */
export const goalBars = (
  goal: FinancialGoal,
  history: HistoryPoint[],
  windowEnd: string,
  today: string
): GoalBar[] => {
  if (!goal.startDate) return [];
  const plan = planOf(goal);
  const yieldPerYear = goal.requiredYieldPerYear ?? 0;

  return periodsOf(plan.startDate, windowEnd).map(
    ({ label, end }, index, all) => {
      const previousEnd = index > 0 ? all[index - 1].end : "";
      const isCurrent = previousEnd < today && today <= end;
      const isFuture = previousEnd >= today;

      if (isCurrent && isRunning(goal, today)) {
        return {
          label,
          actual: goal.actualAssets ?? 0,
          plan: goal.expectedAssets ?? 0,
          isCurrent,
          isFuture,
        };
      }
      const lastValue = history
        .filter(({ date }) => previousEnd < date && date <= end)
        .at(-1);
      return {
        label,
        actual: isFuture || !lastValue ? null : lastValue.value,
        plan: plannedWealthAt(plan, yieldPerYear, end),
        isCurrent,
        isFuture,
      };
    }
  );
};

export const todayIso = (now = new Date()) =>
  toIsoDate(now.getFullYear(), now.getMonth(), now.getDate());
